import type { CredentialResponse } from "@react-oauth/google";
import type { EffectiveThemeMode } from "@replohq/lattice/blocks/theme-picker/palette";

import * as React from "react";

import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { Button } from "@replohq/lattice/components/button/Button";
import { cn } from "@replohq/lattice/utils/cn";
import {
  GOOGLE_AUTH_PROXY_ORIGIN,
  isVercelPreviewDeployment,
} from "@v2/utils/env";
import useMeasure from "react-use-measure";

import { GOOGLE_CLIENT_ID } from "replo-utils/lib/googleAuth";

// The design system's secondary button, so it sits beside the form's primary one as a pair.
function GoogleButton({
  children,
  ...props
}: Pick<
  React.ComponentProps<typeof Button>,
  "aria-hidden" | "children" | "disabled" | "onClick" | "tabIndex"
>) {
  return (
    <Button
      {...props}
      type="button"
      variant="secondary"
      size="lg"
      className="w-full"
      UNSAFE_className="max-w-[400px] text-sm"
      startEnhancer={<GoogleLogo />}
    >
      {children}
    </Button>
  );
}

const GoogleAuth = ({
  handleGoogleCredentials,
  className,
  effectiveMode = "light",
}: {
  handleGoogleCredentials: (
    credentialResponse: CredentialResponse,
  ) => Promise<void>;
  className?: string;
  effectiveMode?: EffectiveThemeMode;
}) => {
  if (isVercelPreviewDeployment()) {
    return (
      <GoogleAuthProxyButton
        handleGoogleCredentials={handleGoogleCredentials}
        className={className}
      />
    );
  }

  return (
    <DirectGoogleAuth
      handleGoogleCredentials={handleGoogleCredentials}
      className={className}
      effectiveMode={effectiveMode}
    />
  );
};

// (Patrick, 2026-03-11): Renders the Google sign-in button directly using
// Google Identity Services. Used on origins that are whitelisted in Google
// Cloud Console (production, localhost).
//
// (2026-10-01): Google's iframe button always paints a white canvas behind
// `filled_black`, which reads as a broken white slab on dark auth. Keep their
// real button for the click/credential flow, but hide it and show a
// lattice-token button that matches the rest of the form.
const DirectGoogleAuth: React.FC<{
  handleGoogleCredentials: (
    credentialResponse: CredentialResponse,
  ) => Promise<void>;
  className?: string;
  effectiveMode: EffectiveThemeMode;
}> = ({ handleGoogleCredentials, className, effectiveMode }) => {
  const [containerRef, { width: containerWidth }] = useMeasure();

  // Note (Kevin, 2026-01-07): Google Identity Services button requires a numeric width.
  // We measure the container and pass that width to make it responsive. The button
  // has a minimum width of 200px per Google's specs, and we cap at 400px for aesthetics.
  // We floor the width to avoid sub-pixel rendering issues.
  const buttonWidth = Math.max(200, Math.min(Math.floor(containerWidth), 400));

  return (
    <div
      ref={containerRef}
      className={cn("relative flex w-full justify-center", className)}
    >
      {/* Only the look: Google's own button sits invisibly on top and takes the click and the focus. */}
      <GoogleButton aria-hidden tabIndex={-1}>
        Continue with Google
      </GoogleButton>
      {containerWidth > 0 && (
        <div className="absolute inset-0 flex justify-center overflow-hidden opacity-0">
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <GoogleLogin
              type="standard"
              shape="rectangular"
              theme={effectiveMode === "dark" ? "filled_black" : "outline"}
              text="continue_with"
              size="large"
              width={buttonWidth}
              logo_alignment="center"
              onSuccess={(credentialResponse: CredentialResponse) => {
                // NOTE (Matt 2024-02-12): GoogleLogin forces us to use a non-async function
                // for the success callback. the `handleGoogleCredentials` function should always
                // catch the error in the defined context. We've added a catch here purely so
                // the linter doesn't yell at us.
                handleGoogleCredentials(credentialResponse).catch((error) => {
                  throw error;
                });
              }}
            />
          </GoogleOAuthProvider>
        </div>
      )}
    </div>
  );
};

// (Patrick, 2026-03-11): Fallback button for Vercel preview deployments where
// Google Identity Services can't render directly (dynamic hostnames aren't
// whitelisted in Google Cloud Console). Opens a popup to the production domain's
// proxy page, which IS whitelisted, and relays the credential back via postMessage.
const GoogleAuthProxyButton: React.FC<{
  handleGoogleCredentials: (
    credentialResponse: CredentialResponse,
  ) => Promise<void>;
  className?: string;
}> = ({ handleGoogleCredentials, className }) => {
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const cleanupRef = React.useRef<(() => void) | null>(null);

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  const handleClick = React.useCallback(() => {
    setError(null);
    setIsLoading(true);

    const returnOrigin = window.location.origin;
    const proxyUrl = `${GOOGLE_AUTH_PROXY_ORIGIN}/auth/google-proxy?returnOrigin=${encodeURIComponent(returnOrigin)}`;

    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const popup = window.open(
      proxyUrl,
      "google-auth-proxy",
      `width=${width},height=${height},left=${left},top=${top},popup=yes`,
    );

    if (!popup) {
      setError("Popup was blocked. Please allow popups for this site.");
      setIsLoading(false);
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      // (Patrick, 2026-03-11): Only accept messages from the trusted proxy origin
      if (event.origin !== GOOGLE_AUTH_PROXY_ORIGIN) {
        return;
      }

      const data = event.data;
      if (data?.type === "google-auth-proxy-success") {
        cleanup();
        handleGoogleCredentials({
          credential: data.credential,
          clientId: data.clientId,
        }).catch((err) => {
          throw err;
        });
      } else if (data?.type === "google-auth-proxy-error") {
        cleanup();
        setError(data.error ?? "Google sign-in failed.");
      }
    };

    const pollTimer = window.setInterval(() => {
      if (popup.closed) {
        cleanup();
      }
    }, 500);

    function cleanup() {
      window.removeEventListener("message", handleMessage);
      window.clearInterval(pollTimer);
      setIsLoading(false);
      cleanupRef.current = null;
    }

    cleanupRef.current = cleanup;
    window.addEventListener("message", handleMessage);
  }, [handleGoogleCredentials]);

  return (
    <div className={cn("flex w-full flex-col items-center gap-2", className)}>
      <GoogleButton onClick={handleClick} disabled={isLoading}>
        {isLoading ? "Connecting..." : "Continue with Google"}
      </GoogleButton>
      {error && <p className="text-sm text-red">{error}</p>}
    </div>
  );
};

function GoogleLogo() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9.003 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9.003 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.712A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.712V4.956H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.046l3.007-2.334z"
        fill="#FBBC05"
      />
      <path
        d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.002 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29c.708-2.127 2.692-3.71 5.036-3.71z"
        fill="#EA4335"
      />
    </svg>
  );
}

export default GoogleAuth;
